import React, { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Search, Filter, MoreHorizontal } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { useTheme } from '@/contexts/ThemeContext'
import { ThemedCard } from '@/components/ui/ThemedCard'

interface Account {
  id: string
  code: string
  name: string
  type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense'
  normal_balance: 'debit' | 'credit'
  parent_account_id?: string
  is_active: boolean
  balance?: number
}

const ChartOfAccountsPage = () => {
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const { theme } = useTheme()

  // Mock data for demo
  useEffect(() => {
    const mockAccounts: Account[] = [
      { id: '1', code: '1001', name: 'Cash - Operating', type: 'asset', normal_balance: 'debit', is_active: true, balance: 50000 },
      { id: '2', code: '1200', name: 'Accounts Receivable', type: 'asset', normal_balance: 'debit', is_active: true, balance: 45230 },
      { id: '3', code: '1300', name: 'Inventory', type: 'asset', normal_balance: 'debit', is_active: true, balance: 67890 },
      { id: '4', code: '1500', name: 'Equipment', type: 'asset', normal_balance: 'debit', is_active: true, balance: 125000 },
      { id: '5', code: '2001', name: 'Accounts Payable', type: 'liability', normal_balance: 'credit', is_active: true, balance: -23450 },
      { id: '6', code: '2100', name: 'Sales Tax Payable', type: 'liability', normal_balance: 'credit', is_active: true, balance: -5420 },
      { id: '7', code: '3001', name: 'Owner\'s Equity', type: 'equity', normal_balance: 'credit', is_active: true, balance: -150000 },
      { id: '8', code: '4001', name: 'Service Revenue', type: 'revenue', normal_balance: 'credit', is_active: true, balance: -89000 },
      { id: '9', code: '5001', name: 'COGS - Parts', type: 'expense', normal_balance: 'debit', is_active: true, balance: 34500 },
      { id: '10', code: '6001', name: 'Wages Expense', type: 'expense', normal_balance: 'debit', is_active: true, balance: 45000 }
    ]
    setAccounts(mockAccounts)
    setLoading(false)
  }, [])

  const filteredAccounts = accounts.filter(account => {
    const matchesSearch = account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         account.code.includes(searchTerm)
    const matchesType = typeFilter === 'all' || account.type === typeFilter
    return matchesSearch && matchesType
  })

  const getTypeStyle = (type: string) => {
    switch (type) {
      case 'asset': 
        return { backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#60a5fa' }
      case 'liability': 
        return { backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#f87171' }
      case 'equity': 
        return { backgroundColor: 'rgba(168, 85, 247, 0.1)', color: '#c084fc' }
      case 'revenue': 
        return { backgroundColor: theme.colors.successBg, color: theme.colors.successText, borderColor: theme.colors.successBorder }
      case 'expense': 
        return { backgroundColor: 'rgba(249, 115, 22, 0.1)', color: '#fb923c' }
      default: 
        return { backgroundColor: theme.colors.bgHover, color: theme.colors.textSecondary }
    }
  }

  const formatBalance = (balance?: number) => {
    if (!balance) return '$0.00'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(Math.abs(balance))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div style={{ color: theme.colors.textSecondary }}>Loading chart of accounts...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold" style={{ color: theme.colors.textPrimary }}>Chart of Accounts</h2>
          <p style={{ color: theme.colors.textSecondary }}>Manage your GAAP account structure</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-md transition-colors"
          style={{ backgroundColor: theme.colors.primaryColor }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryColor}
        >
          <Plus className="h-4 w-4" />
          Add Account
        </button>
      </div>

      {/* Filters */}
      <ThemedCard padding="p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 flex-1">
            <Search className="h-4 w-4" style={{ color: theme.colors.textTertiary }} />
            <input
              type="text"
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
              style={{
                backgroundColor: theme.colors.bgCard,
                borderColor: theme.colors.borderColor,
                color: theme.colors.textPrimary
              }}
            />
          </div>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="asset">Assets</SelectItem>
              <SelectItem value="liability">Liabilities</SelectItem>
              <SelectItem value="equity">Equity</SelectItem>
              <SelectItem value="revenue">Revenue</SelectItem>
              <SelectItem value="expense">Expenses</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </ThemedCard>

      {/* Accounts Table */}
      <ThemedCard padding="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{ backgroundColor: theme.colors.bgHover }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                  Account Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                  Account Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                  Normal Balance
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                  Balance
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: theme.colors.borderColor }}>
              {filteredAccounts.map((account) => (
                <tr 
                  key={account.id}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono font-medium" style={{ color: theme.colors.textPrimary }}>
                      {account.code}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium" style={{ color: theme.colors.textPrimary }}>
                      {account.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span 
                      className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                      style={getTypeStyle(account.type)}
                    >
                      {account.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span 
                      className="text-sm font-medium"
                      style={{ color: account.normal_balance === 'debit' ? '#fb923c' : '#60a5fa' }}
                    >
                      {account.normal_balance}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium" style={{ color: theme.colors.textPrimary }}>
                      {formatBalance(account.balance)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        style={{ color: theme.colors.textTertiary }} 
                        className="transition-opacity"
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        style={{ color: theme.colors.textTertiary }} 
                        className="transition-colors"
                        onMouseEnter={(e) => e.currentTarget.style.color = '#f87171'}
                        onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textTertiary}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ThemedCard>

      {filteredAccounts.length === 0 && (
        <div className="text-center py-12">
          <div style={{ color: theme.colors.textSecondary }}>No accounts found matching your criteria.</div>
        </div>
      )}

      {/* Add Account Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="rounded-lg border p-6 w-full max-w-md" style={{ backgroundColor: theme.colors.bgCard, borderColor: theme.colors.borderColor }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>Add New Account</h3>

            <div className="space-y-4">
              <div>
                <Label htmlFor="accountCode">Account Code</Label>
                <input
                  id="accountCode"
                  type="text"
                  placeholder="e.g., 4001"
                  className="mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    borderColor: theme.colors.borderColor,
                    color: theme.colors.textPrimary
                  }}
                />
              </div>

              <div>
                <Label htmlFor="accountName">Account Name</Label>
                <input
                  id="accountName"
                  type="text"
                  placeholder="e.g., Service Revenue"
                  className="mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    borderColor: theme.colors.borderColor,
                    color: theme.colors.textPrimary
                  }}
                />
              </div>

              <div>
                <Label htmlFor="accountType">Account Type</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asset">Asset</SelectItem>
                    <SelectItem value="liability">Liability</SelectItem>
                    <SelectItem value="equity">Equity</SelectItem>
                    <SelectItem value="revenue">Revenue</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="parentAccount">Parent Account (Optional)</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select parent account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None (Top Level)</SelectItem>
                    <SelectItem value="1000">ASSETS</SelectItem>
                    <SelectItem value="2000">LIABILITIES</SelectItem>
                    <SelectItem value="3000">EQUITY</SelectItem>
                    <SelectItem value="4000">REVENUE</SelectItem>
                    <SelectItem value="5000">COST OF GOODS SOLD</SelectItem>
                    <SelectItem value="6000">EXPENSES</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Optional description..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-opacity hover:opacity-80"
                style={{
                  backgroundColor: theme.colors.bgHover,
                  color: theme.colors.textSecondary
                }}
              >
                Cancel
              </button>
              <button 
                className="flex-1 px-4 py-2 text-white text-sm font-medium rounded-md transition-colors"
                style={{ backgroundColor: theme.colors.primaryColor }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryColor}
              >
                Add Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChartOfAccountsPage
